import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Shield } from "lucide-react";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { fmtDate } from "@/lib/format";

export default function Protection() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const active = !!user?.protection_active;

  const act = async (path) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/protection/${path}`);
      setUser(data);
      toast.success(path === "activate" ? "Protection activated" : "Protection cancelled");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title={t("protection")} subtitle="Add an extra layer of protection to your account." />
      <div className="ec-card p-8 max-w-lg" data-testid="protection-card">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${active ? "bg-white/15" : "bg-white/5"}`}>
          {active ? <ShieldCheck className="w-8 h-8 text-white" /> : <Shield className="w-8 h-8 text-gray-400" />}
        </div>
        <h3 className="font-head text-2xl font-bold text-white mt-5" data-testid="protection-status">
          {active ? "Protection Active" : "Protection Inactive"}
        </h3>
        {active && user?.protection_activated_at && (
          <p className="text-sm text-gray-400 mt-1">Activated: {fmtDate(user.protection_activated_at)}</p>
        )}
        {!active && user?.protection_cancelled_at && (
          <p className="text-sm text-gray-400 mt-1">Cancelled: {fmtDate(user.protection_cancelled_at)}</p>
        )}
        <p className="text-gray-400 mt-4 text-sm leading-relaxed">
          {active
            ? "Your account protection is currently active. You can cancel it anytime below."
            : "Activate protection to secure your account. You can cancel it whenever you like."}
        </p>
        {active ? (
          <Button onClick={() => act("cancel")} disabled={busy} data-testid="cancel-protection-button" className="mt-6 bg-red-600 hover:bg-red-500 font-semibold px-8">
            <ShieldOff className="w-4 h-4 me-2" />{busy ? "..." : "Cancel Protection"}
          </Button>
        ) : (
          <Button onClick={() => act("activate")} disabled={busy} data-testid="activate-protection-button" className="mt-6 bg-orange-600 hover:bg-orange-500 btn-glow font-semibold px-8">
            <ShieldCheck className="w-4 h-4 me-2" />{busy ? "..." : "Activate Protection"}
          </Button>
        )}
      </div>
    </div>
  );
}
